import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, Mail, Mic, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('newsletters');
  const [currentPage, setCurrentPage] = useState({
    newsletters: 1,
    contacts: 1,
    speaking: 1
  });
  const [itemsPerPage] = useState(10);

  const fetchDashboardData = async (page = 1) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard?page=${page}&limit=${itemsPerPage}`, {
        headers: {
          'Authorization': 'Basic ' + btoa('admin:' + password)
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data.data);
      } else {
        setError('Failed to fetch data');
      }
    } catch (err) {
      setError('Failed to connect. Please try again.');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard?page=1&limit=${itemsPerPage}`, {
        headers: {
          'Authorization': 'Basic ' + btoa('admin:' + password)
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data.data);
        setIsAuthenticated(true);
      } else {
        setError('Invalid password');
      }
    } catch (err) {
      setError('Failed to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (tab, newPage) => {
    setCurrentPage(prev => ({ ...prev, [tab]: newPage }));
    await fetchDashboardData(newPage);
  };

  const PaginationControls = ({ tab }) => {
    const pagination = dashboardData?.pagination?.[tab];
    if (!pagination || pagination.total_pages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
        <div className="text-sm text-slate-600">
          Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => handlePageChange(tab, currentPage[tab] - 1)}
            disabled={!pagination.has_prev}
            variant="outline"
            size="sm"
          >
            Previous
          </Button>
          <Button
            onClick={() => handlePageChange(tab, currentPage[tab] + 1)}
            disabled={!pagination.has_next}
            variant="outline"
            size="sm"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Admin Dashboard</CardTitle>
            <p className="text-center text-slate-600 mt-2">Svetlana Foundation</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full"
                />
              </div>
              {error && (
                <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-4">
        <div className="container mx-auto max-w-7xl">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-emerald-100 mt-1">Svetlana Foundation</p>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl p-4 sm:p-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Newsletter Subscribers</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">
                    {dashboardData?.stats.total_newsletters || 0}
                  </p>
                </div>
                <Users className="w-12 h-12 text-emerald-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Contact Messages</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">
                    {dashboardData?.stats.total_contacts || 0}
                  </p>
                </div>
                <Mail className="w-12 h-12 text-blue-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Speaking Requests</p>
                  <p className="text-3xl font-bold text-purple-600 mt-1">
                    {dashboardData?.stats.total_speaking || 0}
                  </p>
                </div>
                <Mic className="w-12 h-12 text-purple-600 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-slate-200">
            <div className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('newsletters')}
                className={`py-4 border-b-2 font-medium transition-colors ${
                  activeTab === 'newsletters'
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                Newsletter ({dashboardData?.stats.total_newsletters || 0})
              </button>
              <button
                onClick={() => setActiveTab('contacts')}
                className={`py-4 border-b-2 font-medium transition-colors ${
                  activeTab === 'contacts'
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                Contact Messages ({dashboardData?.stats.total_contacts || 0})
              </button>
              <button
                onClick={() => setActiveTab('speaking')}
                className={`py-4 border-b-2 font-medium transition-colors ${
                  activeTab === 'speaking'
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                Speaking Requests ({dashboardData?.stats.total_speaking || 0})
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Newsletter Subscribers */}
            {activeTab === 'newsletters' && (
              <div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left border-b border-slate-200">
                        <th className="pb-3 font-semibold text-slate-700">Email</th>
                        <th className="pb-3 font-semibold text-slate-700">Subscribed Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.newsletters.map((subscriber, index) => (
                        <tr key={index} className="border-b border-slate-100">
                          <td className="py-3 text-slate-900">{subscriber.email}</td>
                          <td className="py-3 text-slate-600">
                            {new Date(subscriber.subscribed_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                      {dashboardData?.newsletters.length === 0 && (
                        <tr>
                          <td colSpan="2" className="py-8 text-center text-slate-500">
                            No newsletter subscribers yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <PaginationControls tab="newsletters" />
              </div>
            )}

            {/* Contact Messages */}
            {activeTab === 'contacts' && (
              <div>
                <div className="space-y-4">
                  {dashboardData?.contacts.map((contact, index) => (
                    <Card key={index}>
                      <CardContent className="pt-6">
                        <div className="space-y-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-slate-900">{contact.name}</p>
                              <p className="text-sm text-slate-600">{contact.email}</p>
                            </div>
                            <span className="text-xs text-slate-500">
                              {new Date(contact.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {contact.subject && (
                            <p className="text-sm font-medium text-slate-700">
                              Subject: {contact.subject}
                            </p>
                          )}
                          <p className="text-slate-600">{contact.message}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {dashboardData?.contacts.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      No contact messages yet
                    </div>
                  )}
                </div>
                <PaginationControls tab="contacts" />
              </div>
            )}

            {/* Speaking Requests */}
            {activeTab === 'speaking' && (
              <div>
                <div className="space-y-4">
                  {dashboardData?.speaking.map((request, index) => (
                    <Card key={index}>
                      <CardContent className="pt-6">
                        <div className="space-y-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-slate-900">{request.organization}</p>
                              <p className="text-sm text-slate-600">{request.contact_name}</p>
                            </div>
                            <span className="text-xs text-slate-500">
                              {new Date(request.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-slate-600">Email:</span>{' '}
                              <span className="text-slate-900">{request.email}</span>
                            </div>
                            <div>
                              <span className="text-slate-600">Phone:</span>{' '}
                              <span className="text-slate-900">{request.phone}</span>
                            </div>
                            <div>
                              <span className="text-slate-600">Event Type:</span>{' '}
                              <span className="text-slate-900">{request.event_type}</span>
                            </div>
                            <div>
                              <span className="text-slate-600">Event Date:</span>{' '}
                              <span className="text-slate-900">{request.event_date}</span>
                            </div>
                          </div>
                          {request.topic && (
                            <div className="mt-2">
                              <span className="text-sm text-slate-600">Topic: </span>
                              <span className="text-sm text-slate-900">{request.topic}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {dashboardData?.speaking.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      No speaking requests yet
                    </div>
                  )}
                </div>
                <PaginationControls tab="speaking" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
